import _regeneratorRuntime from 'babel-runtime/regenerator';
import _asyncToGenerator from 'babel-runtime/helpers/asyncToGenerator';
import _extends from 'babel-runtime/helpers/extends';
import _objectWithoutProperties from 'babel-runtime/helpers/objectWithoutProperties';
import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _createClass from 'babel-runtime/helpers/createClass';
import _possibleConstructorReturn from 'babel-runtime/helpers/possibleConstructorReturn';
import _inherits from 'babel-runtime/helpers/inherits';
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Editor, { createEditorStateWithText, composeDecorators } from 'draft-js-plugins-editor';

/* plugins */
import createToolbarPlugin from 'draft-js-static-toolbar-plugin';
import createInlineToolbarPlugin from 'draft-js-inline-toolbar-plugin';
import createImagePlugin from 'draft-js-image-plugin';
import createListDepthPlugin from 'draft-js-list-depth-plugin';
import createResizeablePlugin from 'draft-js-resizeable-plugin';
import createBlockDndPlugin from 'draft-js-drag-n-drop-plugin';
import createFocusPlugin from 'draft-js-focus-plugin';

import createLinkifyPlugin from 'draft-js-linkify-plugin';
import 'draft-js-image-plugin/lib/plugin.css';
import 'draft-js-focus-plugin/lib/plugin.css';
import { convertToRaw, convertFromRaw, EditorState, SelectionState, Modifier } from 'draft-js';
import { createInlineStyleButton, createBlockStyleButton } from 'draft-js-buttons';

import { BLOCK_TYPE, INLINE_STYLE } from './constants';
import { EditorBold, EditorUnderline, EditorItalic, EditorNumberList, EditorSquareList } from '../Icon/icons';
import { Image, handlePaste, ImageAddButton } from './plugins/images';
import Loader from './Loader';
import Link from './Link';
import { utils, translations } from 'gipsy-misc';

import DraftEditorWrapper from './DraftEditorWrapper';
import EditorContext from './EditorContext';

import { getThumbnail, uploadImage } from '../TaskPanel/utils';

/* Draft-js Setup */
var BoldButton = createInlineStyleButton({ style: INLINE_STYLE.BOLD, children: React.createElement(EditorBold, null) });
var UnderlineButton = createInlineStyleButton({ style: INLINE_STYLE.UNDERLINE, children: React.createElement(EditorUnderline, null) });
var ItalicButton = createInlineStyleButton({ style: INLINE_STYLE.ITALIC, children: React.createElement(EditorItalic, null) });
var OrderedListButton = createBlockStyleButton({
  blockType: BLOCK_TYPE.ORDERED_LIST_ITEM,
  children: React.createElement(EditorNumberList, null)
});
var UnorderedListButton = createBlockStyleButton({
  blockType: BLOCK_TYPE.UNORDERED_LIST_ITEM,
  children: React.createElement(EditorSquareList, null)
});

var DraftEditor = function (_PureComponent) {
  _inherits(DraftEditor, _PureComponent);

  function DraftEditor(props) {
    _classCallCheck(this, DraftEditor);

    // initialize plugins
    var _this = _possibleConstructorReturn(this, (DraftEditor.__proto__ || Object.getPrototypeOf(DraftEditor)).call(this, props));

    _initialiseProps.call(_this);

    var toolbarPlugin = props.toolbar === 'default' ? createToolbarPlugin() : createInlineToolbarPlugin();
    var linkifyPlugin = createLinkifyPlugin({
      component: function component(p) {
        var contentState = p.contentState,
            rest = _objectWithoutProperties(p, ['contentState']);

        return React.createElement(Link, _extends({}, rest, { onLinkOpen: _this.onLinkOpen, onFocus: props.onFocus, isMobile: props.isMobile }));
      }
    });
    var resizeablePlugin = createResizeablePlugin({
      vertical: 'absolute',
      horizontal: 'absolute'
    });
    var blockDndPlugin = createBlockDndPlugin();
    var focusPlugin = createFocusPlugin();
    var decorator = composeDecorators(resizeablePlugin.decorator, focusPlugin.decorator, blockDndPlugin.decorator);
    var imagePlugin = createImagePlugin({
      decorator: decorator,
      imageComponent: _this.ImageProxy
    });

    _this.imagePlugin = imagePlugin; // need to store to add images
    var listDepthPlugin = createListDepthPlugin({ maxDepth: 4 });
    _this.plugins = [toolbarPlugin, linkifyPlugin, imagePlugin, listDepthPlugin, resizeablePlugin, focusPlugin, blockDndPlugin];

    _this.state = {
      editorState: _this.getEditorStateFromPropsValue(_this.props.defaultValue),
      hidePlaceholder: false,
      loading: false,
      // we need to store which file id were used to not use the same twice
      // in the mobile logic
      fileIdsUsed: []
    };
    return _this;
  }

  _createClass(DraftEditor, [{
    key: 'componentDidUpdate',
    value: function componentDidUpdate(prevProps) {
      if (prevProps.uploads !== this.props.uploads) {
        // we need to force a re-render otherwise the custom blockRenderFn of the image pluging
        // isn't re-rendered and hence the image don't load on the comments
        // see https://github.com/facebook/draft-js/issues/148 and https://github.com/facebook/draft-js/issues/458
        var editorState = this.state.editorState;
        var content = editorState.getCurrentContent();
        var newEditorState = EditorState.createWithContent(content);
        this.setState({ editorState: newEditorState });
      }
    }
  }, {
    key: 'shouldHidePlaceholder',
    value: function shouldHidePlaceholder(editorState) {
      var contentState = editorState.getCurrentContent();
      return contentState.hasText() || contentState.getBlockMap().first().getType() !== BLOCK_TYPE.UNSTYLED;
    }
  }, {
    key: 'render',
    value: function render() {
      var _props = this.props,
          style = _props.style,
          readOnly = _props.readOnly,
          placeholder = _props.placeholder,
          compact = _props.compact,
          transparent = _props.transparent,
          isNew = _props.isNew,
          padding = _props.padding,
          backgroundHover = _props.backgroundHover,
          uploads = _props.uploads,
          showToolbarInline = _props.showToolbarInline;
      var _state = this.state,
          editorState = _state.editorState,
          hidePlaceholder = _state.hidePlaceholder;

      var Toolbar = this.plugins[0].Toolbar || this.plugins[0].InlineToolbar;
      return React.createElement(
        DraftEditorWrapper,
        {
          style: style || {},
          onClick: this.focus,
          compact: compact,
          showToolbarInline: showToolbarInline,
          transparent: transparent,
          readOnly: readOnly,
          isNew: isNew,
          padding: padding,
          backgroundHover: backgroundHover },
        React.createElement(
          EditorContext.Provider,
          { value: { uploads: uploads || [] } },
          React.createElement(Editor, {
            editorState: editorState,
            placeholder: !hidePlaceholder ? placeholder || '' : '',
            onChange: this.onChange,
            onBlur: this.onBlur,
            keyBindingFn: this.onKeyPress,
            readOnly: readOnly,
            plugins: this.plugins,
            ref: this.setupEditor
          }),
          React.createElement(Toolbar, { children: this.renderButtons }),
          this.state.loading && React.createElement(Loader, { progress: this.state.progress, text: translations.taskPanel.editor.uploadImage })
        )
      );
    }
  }]);

  return DraftEditor;
}(PureComponent);

DraftEditor.propTypes = {
  placeholder: PropTypes.string,
  paramName: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyPress: PropTypes.func,
  onLinkOpen: PropTypes.func,
  readOnly: PropTypes.bool,
  defaultValue: PropTypes.string,
  /* transparent style */
  transparent: PropTypes.bool,
  /* force compact mode */
  compact: PropTypes.bool,
  /* task upload context which is needed to properly display images */
  uploads: PropTypes.array,
  /* triggered when the user pastes an image */
  onImageUpload: PropTypes.func.isRequired,
  uploadUrls: PropTypes.any,
  /* if toolbar should only show upload button */
  toolbarOnlyUploadButton: PropTypes.bool,
  showToolbarInline: PropTypes.bool,

  /* style */
  isNew: PropTypes.bool,
  padding: PropTypes.bool,
  backgroundHover: PropTypes.bool,
  isMobile: PropTypes.bool
};
DraftEditor.defaultProps = {
  readOnly: false,
  toolbarOnlyUploadButton: false,
  showToolbarInline: false,
  toolbar: 'default',
  isMobile: false,
  uploads: []
};

var _initialiseProps = function _initialiseProps() {
  var _this2 = this;

  this.ImageProxy = function (props) {
    var uploads = _this2.props.uploads;

    return React.createElement(Image, _extends({ uploads: uploads }, props));
  };

  this.getEditorStateFromPropsValue = function (value) {
    try {
      var json = JSON.parse(value);
      return EditorState.createWithContent(convertFromRaw(json));
    } catch (err) {
      return createEditorStateWithText(value || '');
    }
  };

  this.getContentFromEditorState = function (editorState) {
    var contentState = editorState.getCurrentContent();
    var stringifiedContent = JSON.stringify(convertToRaw(contentState));

    return { contentState: contentState, stringifiedContent: stringifiedContent };
  };

  this.onChange = function (editorState) {
    var _getContentFromEditor = _this2.getContentFromEditorState(editorState),
        contentState = _getContentFromEditor.contentState,
        stringifiedContent = _getContentFromEditor.stringifiedContent;

    var hidePlaceholder = _this2.shouldHidePlaceholder(editorState);

    _this2.setState({
      editorState: editorState,
      hidePlaceholder: hidePlaceholder
    });

    if (_this2.props.onChange) {
      _this2.props.onChange({
        paramName: _this2.props.paramName,
        value: contentState.hasText() ? stringifiedContent : ''
      });
    }
  };

  this.clear = function () {
    var editorState = _this2.state.editorState;

    var contentState = editorState.getCurrentContent();
    var firstBlock = contentState.getFirstBlock();
    var lastBlock = contentState.getLastBlock();
    var allSelected = new SelectionState({
      anchorKey: firstBlock.getKey(),
      anchorOffset: 0,
      focusKey: lastBlock.getKey(),
      focusOffset: lastBlock.getLength()
    });
    contentState = Modifier.removeRange(contentState, allSelected, 'backward');
    editorState = EditorState.push(editorState, contentState, 'remove-range');
    editorState = EditorState.forceSelection(editorState, contentState.getSelectionAfter());
    _this2.setState({ editorState: editorState, hidePlaceholder: false });
  };

  this.trackUploadProgress = function (_ref) {
    var loaded = _ref.loaded,
        total = _ref.total;

    var progress = Math.round(100.0 * loaded / total);
    _this2.setState({ progress: progress });
  };

  this.uploadFile = function (file) {
    _this2.setState({ loading: true, progress: 0 }, _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var thumbnail, uploadUrls, i, l, value, newFiledIds;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.prev = 0;

              if (_this2.props.onImageUpload) {
                _context.next = 3;
                break;
              }

              throw new Error('(gipsy-ui DraftEditor) onImageUpload undefined');

            case 3:
              _context.next = 5;
              return getThumbnail(file);

            case 5:
              thumbnail = _context.sent;
              _context.next = 8;
              return _this2.props.getUploadUrls();

            case 8:
              uploadUrls = _context.sent;

              if (!(!uploadUrls && _this2.props.isMobile)) {
                _context.next = 20;
                break;
              }

              i = 0, l = 25;

            case 11:
              if (!(i < l)) {
                _context.next = 20;
                break;
              }

              _context.next = 14;
              return utils.delay.wait(200);

            case 14:
              if (!(_this2.props.uploadUrls && !_this2.state.fileIdsUsed.includes(_this2.props.uploadUrls.fileId))) {
                _context.next = 17;
                break;
              }

              uploadUrls = _this2.props.uploadUrls;
              return _context.abrupt('break', 20);

            case 17:
              ++i;
              _context.next = 11;
              break;

            case 20:
              _context.next = 22;
              return uploadImage({ file: file, thumbnail: thumbnail }, uploadUrls, _this2.trackUploadProgress);

            case 22:
              value = _context.sent;

              _this2.props.onImageUpload(value);
              /* TO INVESTIGATE : have to add a 1s timeout for the image to be saved in draftEditor on mobile only */
              setTimeout(function () {
                _this2.onChange(_this2.imagePlugin.addImage(_this2.state.editorState, { fileId: value.fileId }));
              }, _this2.props.isMobile ? 1000 : 0);

              newFiledIds = _this2.state.fileIdsUsed;

              newFiledIds.push(uploadUrls.fileId);

              _this2.setState({ fileIdsUsed: newFiledIds });
              _context.next = 33;
              break;

            case 30:
              _context.prev = 30;
              _context.t0 = _context['catch'](0);

              console.error(_context.t0);

            case 33:
              _context.prev = 33;

              _this2.setState({ loading: false, progress: 0 });
              return _context.finish(33);

            case 36:
            case 'end':
              return _context.stop();
          }
        }
      }, _callee, _this2, [[0, 30, 33, 36]]);
    })));
  };

  this.setupEditor = function (editor) {
    if (!editor) {
      return;
    }
    _this2.editor = editor;
    var editorElement = editor.editor.editor; //wow much editor
    editorElement.addEventListener('paste', function (e) {
      var file = handlePaste(e);
      if (file) {
        _this2.uploadFile(file);
      }
    });
  };

  this.focus = function (e) {
    _this2.editor.focus();
    if (_this2.props.onFocus) {
      _this2.props.onFocus(e);
    }
  };

  this.onBlur = function (e) {
    if (_this2.props.onBlur) {
      _this2.props.onBlur(e);
    }
  };

  this.onLinkOpen = function (url) {
    _this2.blur();
    _this2.props.onLinkOpen && _this2.props.onLinkOpen(url);
  };

  this.blur = function () {
    _this2.editor.blur();
  };

  this.onKeyPress = function (e) {
    if (_this2.props.onKeyPress) {
      return _this2.props.onKeyPress(e);
    }
  };

  this.renderButtons = function (externalProps) {
    var buttons = _this2.props.toolbarOnlyUploadButton ? [React.createElement(ImageAddButton, { key: 'upload', onImageUpload: _this2.uploadFile })] : [React.createElement(BoldButton, _extends({ key: 'bold' }, externalProps)), React.createElement(ItalicButton, _extends({ key: 'italic' }, externalProps)), React.createElement(UnderlineButton, _extends({ key: 'underline' }, externalProps)), React.createElement(UnorderedListButton, _extends({ key: 'unorder' }, externalProps)), React.createElement(OrderedListButton, _extends({ key: 'order' }, externalProps)), React.createElement(ImageAddButton, { key: 'upload', onImageUpload: _this2.uploadFile })];
    return React.createElement(
      'div',
      { className: 'toolbar-buttons', style: { height: '100%' } },
      buttons
    );
  };
};

export default DraftEditor;
//# sourceMappingURL=index.js.map


//////////////////
// WEBPACK FOOTER
// ../gipsy-ui/lib/DraftEditor/index.js
// module id = 831
// module chunks = 0